import { faCheckCircle, faCircleExclamation, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { QuantityPill } from 'components/AddonsMode/QuantityPill/QuantityPill';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { Radio } from 'components/Form/Radio/Radio';
import React from 'react';
import { AddonSchedule, AddonScheduleMode } from 'types/AddonSchedule';
import { DEFAULT_ADDON_QUANTITY } from 'utils/addonSchedule';
import { formatDateRange, formatLongDate, LockedDays, pluralize } from 'utils/addonsMode';

import classes from './ScheduleControls.module.scss';

interface ScheduleControlsProps {
    addonName: string;
    // Eligible days of the group, YYYY-MM-DD, the ones already on the booking included.
    eligibleDates: Array<string>;
    locked: LockedDays;
    // The new selection. Missing when the Add-On is only open because it is already on the booking.
    value?: AddonSchedule;
    // Set once the pet parent tried to move on: a Select Days with no day is then an error.
    showError?: boolean;
    // null drops the new selection.
    onChange: (value: AddonSchedule | null) => void;
}

/**
 * Everyday / Select Days / quantity inside an expanded Add-On card (Figma 1930:6228). Days already
 * on the booking stay in the list, checked and disabled: their quantity only goes up, and Everyday
 * only covers the open ones.
 */
export const ScheduleControls: React.FC<ScheduleControlsProps> = ({
    addonName,
    eligibleDates,
    locked,
    value,
    showError,
    onChange
}) => {
    const openDates = eligibleDates.filter(date => locked[date] === undefined);
    const lockedDates = eligibleDates.filter(date => locked[date] !== undefined);
    const hasLockedDays = lockedDates.length > 0;
    // With nothing new picked yet, the list shows only the days already on the booking.
    const current = value ?? {
        mode: AddonScheduleMode.SelectDays,
        quantity: DEFAULT_ADDON_QUANTITY,
        days: {}
    };

    // A day already on the booking holds the quantity wanted in total, never less than what is saved.
    const lockedRow = (date: string) => {
        const quantity =
            current.mode === AddonScheduleMode.SelectDays
                ? Math.max(current.days[date] ?? 0, locked[date])
                : locked[date];

        const changeQuantity = (next: number) => {
            // Everyday only reaches here next to a single open day: it becomes that day, by day.
            const days =
                current.mode === AddonScheduleMode.Everyday
                    ? Object.fromEntries(openDates.map(open => [open, current.quantity]))
                    : { ...current.days };

            if (next <= locked[date]) {
                delete days[date];
            } else {
                days[date] = next;
            }

            onChange({ ...current, mode: AddonScheduleMode.SelectDays, days });
        };

        return (
            <li key={date} className={clsx(classes.row, classes.rowSelected)}>
                <Checkbox
                    label={formatLongDate(date)}
                    checked
                    disabled
                    className={classes.checkbox}
                />
                <QuantityPill
                    label={`${addonName} quantity on ${formatLongDate(date)}, ${
                        locked[date]
                    } already on this booking`}
                    value={quantity}
                    min={locked[date]}
                    onChange={changeQuantity}
                />
            </li>
        );
    };

    // Plain helper text, like "Pick at least one day": a box would stack on Everyday's chip.
    const notice = hasLockedDays && (
        <p className={classes.notice} role="note">
            <FontAwesomeIcon icon={faCircleInfo} className={classes.noticeIcon} />
            <span>Days already on this booking can&apos;t be removed or reduced.</span>
        </p>
    );

    // Every day is already on the booking: it reads as Everyday, with nothing left to change.
    if (openDates.length === 0 && hasLockedDays) {
        return (
            <div className={classes.root}>
                <div className={classes.modeRow} role="radiogroup" aria-label={`${addonName} days`}>
                    <div className={classes.modes}>
                        <Radio label="Everyday" checked onChange={() => undefined} />
                        <Radio label="Select Days" checked={false} onChange={() => undefined} />
                    </div>
                </div>

                <ul className={classes.rows}>{eligibleDates.map(lockedRow)}</ul>
                {notice}
            </div>
        );
    }

    // A single open day needs no mode choice, only its quantity.
    if (openDates.length <= 1) {
        const singleRow = (date: string) => {
            // Next to days already on the booking, the open day is a checkbox like them.
            if (hasLockedDays || !value) {
                const selected =
                    current.mode === AddonScheduleMode.Everyday && value
                        ? true
                        : current.days[date] !== undefined;
                const quantity =
                    current.mode === AddonScheduleMode.Everyday
                        ? current.quantity
                        : current.days[date];
                const toDays = () =>
                    current.mode === AddonScheduleMode.Everyday ? {} : { ...current.days };
                const setQuantity = (next: number | null) => {
                    const days = toDays();

                    if (next === null) {
                        delete days[date];
                    } else {
                        days[date] = next;
                    }

                    onChange({ ...current, mode: AddonScheduleMode.SelectDays, days });
                };

                return (
                    <li
                        key={date}
                        className={clsx(classes.row, { [classes.rowSelected]: selected })}
                    >
                        <Checkbox
                            label={formatLongDate(date)}
                            checked={selected}
                            className={classes.checkbox}
                            onChange={(_event, checked) =>
                                setQuantity(checked ? DEFAULT_ADDON_QUANTITY : null)
                            }
                        />
                        {selected && (
                            <QuantityPill
                                label={`${addonName} quantity`}
                                value={quantity}
                                onChange={setQuantity}
                            />
                        )}
                    </li>
                );
            }

            return (
                <li key={date} className={clsx(classes.row, classes.rowSelected)}>
                    <span className={classes.rowLabel}>{formatLongDate(date)}</span>
                    <QuantityPill
                        label={`${addonName} quantity`}
                        value={value.quantity}
                        onChange={quantity =>
                            onChange({ ...value, mode: AddonScheduleMode.Everyday, quantity })
                        }
                    />
                </li>
            );
        };

        return (
            <div className={classes.root}>
                <ul className={classes.rows}>
                    {eligibleDates.map(date =>
                        locked[date] !== undefined ? lockedRow(date) : singleRow(date)
                    )}
                </ul>
                {notice}
            </div>
        );
    }

    const toggleDay = (date: string, checked: boolean) => {
        const days = { ...current.days };

        if (checked) {
            days[date] = DEFAULT_ADDON_QUANTITY;
        } else {
            delete days[date];
        }

        onChange({ ...current, days });
    };

    const isEveryday = current.mode === AddonScheduleMode.Everyday;

    return (
        <div className={classes.root}>
            <div className={classes.modeRow} role="radiogroup" aria-label={`${addonName} days`}>
                <div className={classes.modes}>
                    <Radio
                        label="Everyday"
                        checked={isEveryday}
                        onChange={() => onChange({ ...current, mode: AddonScheduleMode.Everyday })}
                    />
                    <Radio
                        label="Select Days"
                        checked={!isEveryday}
                        onChange={() =>
                            onChange({ ...current, mode: AddonScheduleMode.SelectDays })
                        }
                    />
                </div>

                {isEveryday && (
                    <QuantityPill
                        label={`${addonName} quantity`}
                        value={current.quantity}
                        onChange={quantity => onChange({ ...current, quantity })}
                    />
                )}
            </div>

            {isEveryday ? (
                <p className={classes.everydayChip}>
                    <FontAwesomeIcon icon={faCheckCircle} className={classes.lockedIcon} />
                    <span>
                        All {pluralize(eligibleDates.length, 'booking day')} ·{' '}
                        {formatDateRange(eligibleDates)}
                    </span>
                </p>
            ) : (
                <>
                    <ul className={classes.rows}>
                        {eligibleDates.map(date => {
                            if (locked[date] !== undefined) {
                                return lockedRow(date);
                            }

                            const selected = current.days[date] !== undefined;

                            return (
                                <li
                                    key={date}
                                    className={clsx(classes.row, {
                                        [classes.rowSelected]: selected
                                    })}
                                >
                                    <Checkbox
                                        label={formatLongDate(date)}
                                        checked={selected}
                                        className={classes.checkbox}
                                        onChange={(_event, checked) => toggleDay(date, checked)}
                                    />
                                    {selected && (
                                        <QuantityPill
                                            label={`${addonName} quantity on ${formatLongDate(
                                                date
                                            )}`}
                                            value={current.days[date]}
                                            onChange={quantity =>
                                                onChange({
                                                    ...current,
                                                    days: { ...current.days, [date]: quantity }
                                                })
                                            }
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    {value &&
                        Object.keys(value.days).length === 0 &&
                        (showError ? (
                            <p role="alert" className={classes.error} data-schedule-error>
                                <FontAwesomeIcon icon={faCircleExclamation} />
                                <span>Pick at least one day</span>
                            </p>
                        ) : (
                            <p className={classes.helper}>Pick at least one day.</p>
                        ))}
                </>
            )}

            {notice}
        </div>
    );
};
